import React from "react"

import { Grid } from "@mui/material"

import {
    ErrorResponse,
    Loading,
    RestResponse,
} from "@sinossi/mates-react-library"

import { RawDraftContentState } from "draft-js"

import {
    Action, AssignableUsers, AssignRequest,
    PaginatedResponse,
    SearchFilter, UpdateActionRequest,
    Workflow,
    WorkflowActionsResponse,
} from "models"
import { WorkflowService } from "services/workflow.service"
import withUseRouter, { UseRouterParams } from "services/router"
import {
    MatesComponent,
    MatesComponentState,
} from "components/mates-component"

import {
    AssignWorkflow,
    DeleteWorkflow,
    AssignWorkflowAction,
    NewStep,
    DeferAction,
} from "../modals"
import { WorkflowColumnOne } from "./workflow-columns/workflow-column-one"
import { WorkflowColumnTwo } from "./workflow-columns/workflow-column-two"
import { WorkflowColumnThree } from "./workflow-columns/workflow-column-three"
import { MarkActionAsDone } from "../modals/mark-action-as-done";
import { SbSnackbar } from "../../commons/sb-snackbar";
import { handleDeferErrorMessages } from "../modals/defer-action/defer-error-messages";

interface WorkflowSectionProps extends UseRouterParams {
    handlePagination: (request: SearchFilter) => void
    workflowsData: PaginatedResponse<Workflow>
    usersData: AssignableUsers[]
}
interface WorkflowSectionState extends MatesComponentState {
    loading: boolean
    workflowActions: WorkflowActionsResponse | null
    selectedWorkflow: Workflow | null
    selectedAction: Action | null
    openAssignWorkflowDialog: boolean
    openDeleteWorkflowDialog: boolean
    openAssignWorkflowActionDialog: boolean
    openCreateActionDialog: boolean
    openEditActionDialog: boolean
    openDeferActionDialog: boolean
    openMarkActionAsDoneDialog: boolean
    assignWorkflow: Workflow | null
    deleteWorkflow: Workflow | null
    snackbarData: {
        openNotification: boolean,
        severity: 'error' | 'info' | 'success' | 'warning',
        notificationMessage: string
    }
}
class WorkflowSection extends React.Component<WorkflowSectionProps, WorkflowSectionState> {

    private handleDeferError = (details: { [id: string]: string } | null, response: RestResponse<any, ErrorResponse>) => {
        const errorMessage = handleDeferErrorMessages(details)
        this.handleOpenNotification(errorMessage.message, errorMessage.severity)
        this.setState(() => ({
            loading: false
        }))
    }

    private readonly matesComponent: MatesComponent<WorkflowSectionProps, WorkflowSectionState> = new MatesComponent(this, this.handleDeferError)

    constructor(props: WorkflowSectionProps) {
        super(props)
        this.state = {
            ...this.matesComponent.setupState(),
            loading: false,
            workflowActions: null,
            selectedWorkflow: null,
            selectedAction: null,
            openAssignWorkflowDialog: false,
            openDeleteWorkflowDialog: false,
            openAssignWorkflowActionDialog: false,
            openCreateActionDialog: false,
            openEditActionDialog: false,
            openDeferActionDialog: false,
            openMarkActionAsDoneDialog: false,
            assignWorkflow: null,
            deleteWorkflow: null,
            snackbarData: {
                openNotification: false,
                severity: "info",
                notificationMessage: ""
            }
        }
    }

    private handleOpenNotification = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
        this.setState(() => ({
            snackbarData: {
                openNotification: true,
                notificationMessage: message,
                severity: severity
            }
        }))
    };

    private handleCloseNotification = () => {
        this.setState(() => ({
            snackbarData: {
                ...this.state.snackbarData,
                openNotification: false
            }
        }))
    };


    private handleWorkflowSelection = (workflow: Workflow) => {
        if (this.state.selectedWorkflow && workflow.id === this.state.selectedWorkflow.id) {
            return
        }

        this.setState(
            () => ({
                loading: true,
                selectedWorkflow: workflow,
            }),
            () => WorkflowService.getWorkflowActionList(
                workflow.id,
                this.getWorkflowActionListCallback,
                this.matesComponent.onException,
            ),
        )
    }

    private onActionSelected = (action: Action) => {
        if (!this.state.workflowActions) {
            return
        }

        this.setState(() => ({ selectedAction: action }))
    }

    private getWorkflowActionListCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        let i: number
        let tmp: any = Object.assign({}, response.data);

        if (tmp) {
            for (i = 0; i < tmp.actions.length; i++) {
                if (tmp.actions[i].content.blocks === undefined) {
                    tmp.actions[i].content = {
                        blocks: [
                            {
                                key: "js9m",
                                text: "",
                                type: "unstyled",
                                depth: 0,
                                inlineStyleRanges: [],
                                entityRanges: [],
                                data: {}
                            }
                        ],
                        entityMap: {}
                    }
                }
            }
        }


        this.setState(() => ({
            workflowActions: tmp as WorkflowActionsResponse,
            loading: false,
        }))
    }

    private openAssignWorkflowDialog = (workflow: Workflow) => {
        this.setState(() => ({
            openAssignWorkflowDialog: true,
            assignWorkflow: workflow,
        }))
    }

    private openDeleteWorkflowDialog = (workflow: Workflow) => {
        this.setState(() => ({
            openDeleteWorkflowDialog: true,
            deleteWorkflow: workflow,
        }))
    }

    private closeAssignWorkflowDialog = () => {
        this.setState(() => ({
            openAssignWorkflowDialog: false,
            assignWorkflow: null,
        }))
    }

    private closeDeleteWorkflowDialog = () => {
        this.setState(() => ({
            openDeleteWorkflowDialog: false,
            deleteWorkflow: null,
        }))
    }

    private openAssignWorkflowActionDialog = (action: Action) => {
        this.setState(() => ({
            openAssignWorkflowActionDialog: true,
            selectedAction: action,
        }))
    }

    private closeAssignWorkflowActionDialog = () => {
        this.setState(() => ({
            openAssignWorkflowActionDialog: false,
        }))
    }

    private openCreateActionDialog = () => {
        this.setState(() => ({
            openCreateActionDialog: true,
        }))
    }

    private closeCreateActionDialog = () => {
        this.setState(() => ({
            openCreateActionDialog: false,
        }))
    }

    private openEditActionDialog = () => {
        this.setState(() => ({
            openEditActionDialog: true,
        }))
    }

    private closeEditActionDialog = () => {
        this.setState(() => ({
            openEditActionDialog: false,
        }))
    }

    private openDeferActionDialog = (action: Action) => {
        this.setState(() => ({
            openDeferActionDialog: true,
            selectedAction: action,
        }))
    }

    private closeDeferActionDialog = () => {
        this.setState(() => ({
            openDeferActionDialog: false,
            selectedAction: null,
        }))
    }

    private handlePagination = (value: number) => {
        let request: SearchFilter = {
            pageSize: 10,
            pageId: value - 1,
            orderType: null,
            orderBy: null
        }
        this.props.handlePagination(request)
    }

    private defaultHandlePagination = () => {
        let request: SearchFilter = {
            pageSize: 10,
            pageId: 0,
            orderType: null,
            orderBy: null,
        }

        this.setState(() => ({
            loading: false,
        }), () => this.props.handlePagination(request))
    }

    private submitNewAction = (values: any) => {
        this.closeCreateActionDialog()

        this.setState(() => ({ loading: true }))
        WorkflowService.createNewAction(
            values,
            this.state.selectedWorkflow!.id,
            this.submitNewActionCallback,
            this.matesComponent.onException,
        )
    }

    private submitNewActionCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }
        this.defaultHandlePagination()
    }

    private deleteWorkflow = (id: string) => {
        this.closeDeleteWorkflowDialog()

        this.setState(
            () => ({
                loading: true,
            }),
            () => WorkflowService.deleteWorkflow(
                id,
                this.deleteWorkflowCallback,
                this.matesComponent.onException,
            ),
        )
    }

    private deleteWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            loading: false,
        }), () => this.defaultHandlePagination())
    }

    private openMarkActionAsDone = (action: Action) => {
        this.setState(() => ({
            openMarkActionAsDoneDialog: true,
            selectedAction: action
        }))
    }

    private closeMarkActionAsDone = () => {
        this.setState(() => ({
            openMarkActionAsDoneDialog: false
        }))
    }

    private markActionAsDone = () => {
        if (this.state.selectedAction) {
            this.setState(
                () => ({
                    loading: true,
                }),
                () => WorkflowService.doneAction(
                    this.state.selectedAction!.id,
                    this.doneActionCallback,
                    this.matesComponent.onError
                )
            )
        }
    }

    private doneActionCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            loading: false,
        }), () => this.defaultHandlePagination())
    }

    private submitDeferAction = (values: any) => {
        if (!this.state.selectedAction) {
            return
        }

        const request: { deadline: string } = { deadline: values.deadline }
        const action: Action = this.state.selectedAction

        this.setState(
            () => ({
                loading: true,
            }),
            () => WorkflowService.deferAction(
                action.id,
                request,
                this.submitDeferActionCallback,
                this.matesComponent.onError,
            ),
        )
    }

    private submitDeferActionCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            loading: false,
        }))
        this.defaultHandlePagination()
        this.closeDeferActionDialog()
    }

    private updateActionContent = (action: Action, request: UpdateActionRequest) => {
        this.setState(
            () => ({
                loading: true,
            }),
            () => WorkflowService.updateActionContent(
                action.id,
                request,
                this.updateActionContentCallback,
                this.matesComponent.onException,
            ),
        )
    }

    private updateActionContentCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.setState(() => ({
            loading: false,
        }), () => this.defaultHandlePagination())
    }

    private skipAction = (actionId: string) => {
        WorkflowService.skipAction(actionId, this.skipActionCallback, this.matesComponent.onException)
    }

    private skipActionCallback = (response: any) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        if (this.state.selectedWorkflow) {
            WorkflowService.getWorkflowActionList(
                this.state.selectedWorkflow.id,
                this.getWorkflowActionListCallback,
                this.matesComponent.onException,
            )
        }
    }

    private assignWorkflow = (workflowId: string, userId: string) => {
        let request: AssignRequest = {
            executor: userId
        }

        WorkflowService.assignWorkflow(workflowId, request, this.assignWorkflowCallback, this.matesComponent.onError)
    }

    private assignWorkflowCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    private assignWorkflowAction = (actionId: string, userId: string) => {
        let request: AssignRequest = {
            executor: userId
        }
        WorkflowService.assignWorkflowAction(actionId, request, this.assignWorkflowActionCallback, this.matesComponent.onError)
    }

    private assignWorkflowActionCallback = (response: RestResponse<any, ErrorResponse>) => {
        if (response.hasError()) {
            this.matesComponent.onError(response)
            return
        }

        this.defaultHandlePagination()
    }

    render() {
        return (
            <>
                <Loading show={this.state.loading} />
                {this.props.workflowsData &&
                    <Grid container alignItems={"start"} columnSpacing={3} height={"65vh"}>
                        <Grid item sm={12} md={4}>
                            <WorkflowColumnOne
                                handlePagination={this.handlePagination}
                                onItemSelected={this.handleWorkflowSelection}
                                openAssignWorkflowDialog={this.openAssignWorkflowDialog}
                                openDeleteWorkflowDialog={this.openDeleteWorkflowDialog}
                                assignWorkflow={this.assignWorkflow}
                                selectedWorkflowId={this.state.selectedWorkflow ? this.state.selectedWorkflow!.id : null}
                                workflowsData={this.props.workflowsData} />
                        </Grid>
                        <Grid item sm={12} md={4}>
                            {this.state.workflowActions !== null &&
                                <WorkflowColumnTwo
                                    openAssignWorkflowActionDialog={this.openAssignWorkflowActionDialog}
                                    openDeferActionDialog={this.openDeferActionDialog}
                                    openCreateActionDialog={this.openCreateActionDialog}
                                    openEditActionDialog={this.openEditActionDialog}
                                    onActionSelected={this.onActionSelected}
                                    workflow={this.state.selectedWorkflow}
                                    markActionAsDone={this.openMarkActionAsDone}
                                    skipAction={this.skipAction}
                                    selectedAction={this.state.selectedAction}
                                    workflowActions={this.state.workflowActions!} />
                            }
                        </Grid>
                        <Grid item sm={12} md={4}>
                            {this.state.selectedAction !== null &&
                                <WorkflowColumnThree
                                    action={this.state.selectedAction}
                                    openAssignWorkflowActionDialog={this.openAssignWorkflowActionDialog}
                                    openDeferActionDialog={this.openDeferActionDialog}
                                    openSkipDialog={this.skipAction}
                                    markActionAsDone={this.openMarkActionAsDone}
                                    updateActionContent={this.updateActionContent} />
                            }
                        </Grid>
                    </Grid>
                }

                {this.state.assignWorkflow &&
                    <AssignWorkflow
                        openDialog={this.state.openAssignWorkflowDialog}
                        onClose={this.closeAssignWorkflowDialog}
                        onAssign={this.assignWorkflow}
                        data={this.state.assignWorkflow}
                        users={this.props.usersData}
                    />
                }
                {this.state.deleteWorkflow &&
                    <DeleteWorkflow openDialog={this.state.openDeleteWorkflowDialog}
                        onClose={this.closeDeleteWorkflowDialog}
                        data={this.state.deleteWorkflow}
                        delete={this.deleteWorkflow}
                    />
                }
                {this.state.selectedAction &&
                    <AssignWorkflowAction openDialog={this.state.openAssignWorkflowActionDialog}
                        onClose={this.closeAssignWorkflowActionDialog}
                        assign={this.assignWorkflowAction}
                        data={this.state.selectedAction}
                        users={this.props.usersData}
                    />
                }
                {this.state.selectedAction &&
                    <DeferAction
                        openDialog={this.state.openDeferActionDialog}
                        onClose={this.closeDeferActionDialog}
                        data={this.state.selectedAction}
                        submit={this.submitDeferAction}
                    />
                }
                {
                    this.state.openCreateActionDialog &&
                    <NewStep openDialog={this.state.openCreateActionDialog}
                        onClose={this.closeCreateActionDialog}
                        data={null}
                        create
                        action
                        submit={this.submitNewAction}
                    />
                }
                {
                    this.state.openEditActionDialog && this.state.selectedAction &&
                    <NewStep openDialog={this.state.openEditActionDialog}
                        onClose={this.closeEditActionDialog}
                        data={this.state.selectedAction}
                        edit
                        action
                        submit={this.submitNewAction}
                    />
                }
                {
                    this.state.openMarkActionAsDoneDialog && this.state.selectedAction &&
                    <MarkActionAsDone onClose={this.closeMarkActionAsDone}
                        action={this.state.selectedAction}
                        submit={this.markActionAsDone}
                    />
                }
                <SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />
            </>
        )
    }
}

export default withUseRouter(WorkflowSection)
